import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '@/App';
import Navbar from '@/components/Navbar';
import { API, BACKEND_URL, resolveImg } from '@/config';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Calendar, User, Edit, Trash2 } from 'lucide-react';

const BlogPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useContext(AuthContext);
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isAdmin = user?.role === 'admin';
  const isAuthor = user?.user_id === post?.author_id;
  const canEdit = isAdmin || isAuthor;

  useEffect(() => {
    fetchPost();
  }, [id]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/blog/${id}`);
      setPost(response.data);
    } catch (error) {
      toast.error('Post no encontrado');
      navigate('/blog');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/blog/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post eliminado');
      navigate('/blog');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
        <Navbar />
        <div className="flex justify-center py-32">
          <div className="w-10 h-10 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!post) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50" data-testid="blog-post-page">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16">
        {/* Back link */}
        <Link
          to="/blog"
          className="inline-flex items-center gap-1.5 text-amber-700 hover:text-amber-900 mb-6 text-sm font-medium transition-colors"
        >
          <ArrowLeft size={16} />
          Volver al Blog
        </Link>

        <article className="bg-white rounded-3xl shadow-lg border-2 border-amber-100 overflow-hidden">
          {/* Post Header */}
          <div className="p-8 sm:p-10">
            <h1
              className="text-3xl sm:text-4xl font-bold text-amber-900 leading-tight"
              data-testid="post-title"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              {post.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-amber-700/80">
              <span className="flex items-center gap-1.5">
                <User size={14} />
                {post.author_name}
              </span>
              <span className="flex items-center gap-1.5">
                <Calendar size={14} />
                {formatDate(post.created_at)}
              </span>
            </div>

            {/* Admin / Author actions */}
            {canEdit && (
              <div className="flex gap-2 mt-5" data-testid="post-actions">
                <button
                  data-testid="edit-post-btn"
                  onClick={() => navigate(`/blog/edit/${post.id}`)}
                  className="inline-flex items-center gap-1.5 text-sm px-4 py-2 bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 rounded-full font-medium transition-colors"
                >
                  <Edit size={14} /> Editar
                </button>
                <button
                  data-testid="delete-post-btn"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="inline-flex items-center gap-1.5 text-sm px-4 py-2 bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 rounded-full font-medium transition-colors"
                >
                  <Trash2 size={14} /> Eliminar
                </button>
              </div>
            )}

            {/* Delete Confirmation */}
            {showDeleteConfirm && (
              <div className="mt-5 p-4 bg-red-50 border-2 border-red-200 rounded-2xl" data-testid="delete-confirm">
                <p className="text-red-800 text-sm mb-3 font-medium">
                  ¿Estás seguro de que quieres eliminar este post? Esta acción no se puede deshacer.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={handleDelete}
                    data-testid="confirm-delete-btn"
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-full text-sm font-medium transition-colors"
                  >
                    Sí, eliminar
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded-full text-sm font-medium transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            {/* Divider */}
            <div className="flex items-center gap-2 my-8">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-300 to-transparent"></div>
              <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-300 to-transparent"></div>
            </div>

            {/* Post Content */}
            <div
              className="prose prose-amber max-w-none text-gray-800 leading-relaxed whitespace-pre-wrap text-base"
              data-testid="post-content"
            >
              {post.content}
            </div>

            {/* Post Images */}
            {post.images && post.images.length > 0 && (
              <div className="mt-8 space-y-4" data-testid="post-images">
                {post.images.map((img, idx) => (
                  <img
                    key={idx}
                    src={resolveImg(img)}
                    alt={`${post.title} - imagen ${idx + 1}`}
                    className="w-full rounded-2xl border-2 border-amber-100 object-cover max-h-[600px] shadow-md"
                  />
                ))}
              </div>
            )}
          </div>
        </article>
      </div>
    </div>
  );
};

export default BlogPost;
